/* eslint-disable react-hooks/exhaustive-deps */
import { useState, useMemo, useRef, useEffect } from "react";
import {
  ResponsiveContainer,
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
} from "recharts";
import { User, TrendingUp, Target, CheckCircle, AlertTriangle } from "lucide-react";

import LeadGateModal from "../Modals/LeadGateModal.jsx";
import SuccessModal from "../Modals/SuccessModal.jsx";
import ConfirmSendResultsModal from "../Modals/ConfirmSendResultsModal.jsx";
import UpdateEmailModal from "../Modals/UpdateEmailModal.jsx";

import { detectToolsOrigin } from "../../../utils/helper.js";
import axios from "axios";

import "../calculator.scss";


const RETIREMENT_DISCLAIMER_TEXT =
  "Key Assumptions & Important Notes\n\n" +
  "Calculation Assumptions:\n" +
  "• Retirement duration: 25 years (adjustable)\n" +
  "• Contributions made annually at year-end\n" +
  "• Returns compounded annually\n" +
  "• Inflation applied to desired income projections\n" +
  "• Constant contribution and return rates assumed\n\n" +
  "Important Disclaimers:\n" +
  "• Tax implications not included in calculations\n" +
  "• Market volatility not factored into projections\n" +
  "• Healthcare and long-term care costs not included\n" +
  "• Social Security benefits not considered\n" +
  "• Professional tax planning can optimize your strategy\n\n" +
  "This calculator provides estimates for planning purposes only. Actual outcomes may vary based on market conditions, taxes, fees, and individual circumstances.";


const pow = (a, b) => Math.pow(a, b);

// ===== YEAR CONFIG (same pattern) =====
const YEAR_CONFIG = {
  2025: {
    disclaimer:
      "* This calculator provides estimates for planning purposes only. Actual outcomes may vary based on market conditions, taxes, fees, and individual circumstances.",
  },
  2026: {
    disclaimer:
      "* This calculator provides estimates for planning purposes only. Actual outcomes may vary based on market conditions, taxes, fees, and individual circumstances.",
  },
};

// ===== Notes block (screenshot) — single source of truth =====
const ASSUMPTIONS_NOTES = {
  title: "Key Assumptions & Important Notes",
  leftTitle: "Calculation Assumptions",
  rightTitle: "Important Disclaimers",
  assumptions: [
    "Retirement duration: 25 years (adjustable)",
    "Contributions made annually at year-end",
    "Returns compounded annually",
    "Inflation applied to desired income projections",
    "Constant contribution and return rates assumed",
  ],
  disclaimers: [
    "Tax implications not included in calculations",
    "Market volatility not factored into projections",
    "Healthcare and long-term care costs not included",
    "Social Security benefits not considered",
    "Professional tax planning can optimize your strategy",
  ],
};

// Accumulated balance after n years with annual contribution C at end of year
const futureValue = (P, C, r, n) => {
  if (n <= 0) return P;
  if (Math.abs(r) < 1e-12) return P + C * n;
  const growth = pow(1 + r, n);
  const af = (growth - 1) / r;
  return P * growth + C * af;
};

// Present value of N-year annuity with first payment I1 in year 1,
// growing at rate g, discounted at rate r (handles r≈g)
const pvGrowingAnnuity = (I1, r, g, N) => {
  if (I1 <= 0 || N <= 0) return 0;
  if (Math.abs(r - g) < 1e-12) return (I1 * N) / (1 + r);
  const ratio = (1 + g) / (1 + r);
  return (I1 * (1 - pow(ratio, N))) / (r - g);
};

const PROXY_URL =
  window.__UK_PROXY_URL__ || `${detectToolsOrigin()}/.netlify/functions/api-proxy`;

function RetirementSavingsTaxCalculator({
  year = 2025,
  calculatorName = "Retirement Savings Calculator",
  endpoint = PROXY_URL,
}) {
  const yearCfg = YEAR_CONFIG[year] || YEAR_CONFIG[2025];

  const [formData, setFormData] = useState({
    currentAge: "",
    retirementAge: "",
    currentSavings: "",
    annualContribution: "",
    preRetirementReturn: "",
    postRetirementReturn: "",
    inflationRate: "",
    desiredAnnualIncome: "",
    retirementDuration: "",
  });

  const [results, setResults] = useState(null);
  const [hasCalculated, setHasCalculated] = useState(false);

  // ✅ include pdf_notes now
  const latestPayloadRef = useRef({
    inputs_json: null,
    results_json: null,
    pdf_notes: null,
  });

  const resultsRef = useRef(null);

  // ---------- Gating storage helpers (24h, shared across calculators) ----------
  const UNK_GATE_KEY = "unk_calc_unlock_v1";
  const UNK_GATE_TTL_MS = 24 * 60 * 60 * 1000;

  const readUnlock = () => {
    try {
      const raw = localStorage.getItem(UNK_GATE_KEY);
      if (!raw) return { unlocked: false, email: "" };
      const parsed = JSON.parse(raw);
      const expiresAt = Number(parsed?.expiresAt || 0);
      const email = String(parsed?.email || "");
      if (!expiresAt || Date.now() > expiresAt) {
        localStorage.removeItem(UNK_GATE_KEY);
        return { unlocked: false, email: "" };
      }
      return { unlocked: true, email };
    } catch {
      return { unlocked: false, email: "" };
    }
  };

  const writeUnlock = ({ email }) => {
    const payload = {
      expiresAt: Date.now() + UNK_GATE_TTL_MS,
      email: String(email || ""),
    };
    localStorage.setItem(UNK_GATE_KEY, JSON.stringify(payload));
  };

  const pageUrl = typeof window !== "undefined" ? window.location.href : "";

  // gate state
  const [unlocked, setUnlocked] = useState(false);
  const [storedEmail, setStoredEmail] = useState("");

  // modals
  const [showLeadModal, setShowLeadModal] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [showUpdateEmailModal, setShowUpdateEmailModal] = useState(false);

  const [sendingResults, setSendingResults] = useState(false);

  const params = useMemo(() => new URLSearchParams(window.location.search), []);
  const qa = useMemo(() => params.has("qa_mode"), [params]);

  useEffect(() => {
    const gate = readUnlock();
    setUnlocked(gate.unlocked);
    setStoredEmail(gate.email || "");
  }, []);

  // Prevent infinite loops by stabilizing the formData reference
  const stableFormData = useMemo(
    () => formData,
    [
      formData.currentAge,
      formData.retirementAge,
      formData.currentSavings,
      formData.annualContribution,
      formData.preRetirementReturn,
      formData.postRetirementReturn,
      formData.inflationRate,
      formData.desiredAnnualIncome,
      formData.retirementDuration,
    ]
  );

  // ✅ UX: if they edit after calculating, hide results until they click again
  useEffect(() => {
    setHasCalculated(false);
    setResults(null);
    latestPayloadRef.current = { inputs_json: null, results_json: null, pdf_notes: null };
  }, [
    formData.currentAge,
    formData.retirementAge,
    formData.currentSavings,
    formData.annualContribution,
    formData.preRetirementReturn,
    formData.postRetirementReturn,
    formData.inflationRate,
    formData.desiredAnnualIncome,
    formData.retirementDuration,
    year,
  ]);

  const updateFormData = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  // ✅ payload includes notes now (like screenshot)
  const computePayload = () => {
    let {
      currentAge,
      retirementAge,
      currentSavings,
      annualContribution,
      preRetirementReturn,
      postRetirementReturn,
      inflationRate,
      desiredAnnualIncome,
      retirementDuration,
    } = stableFormData;

    currentAge = Number(currentAge) || 0;
    retirementAge = Number(retirementAge) || 0;
    currentSavings = Number(currentSavings) || 0;
    annualContribution = Number(annualContribution) || 0;
    preRetirementReturn = Number(preRetirementReturn) || 0;
    postRetirementReturn = Number(postRetirementReturn) || 0;
    inflationRate = Number(inflationRate) || 0;
    desiredAnnualIncome = Number(desiredAnnualIncome) || 0;
    retirementDuration = Number(retirementDuration) || 0;

    currentAge = Math.max(0, currentAge);
    retirementAge = Math.max(0, retirementAge);
    retirementDuration = Math.max(0, retirementDuration);

    let yearsToRetirement = Math.floor(retirementAge - currentAge);
    let invalidAges = "";
    if (yearsToRetirement < 0) {
      invalidAges = "Retirement age must be greater than current age.";
      yearsToRetirement = 0;
    }

    const r_pre = (preRetirementReturn || 0) / 100;
    const r_post = (postRetirementReturn || 0) / 100;
    const g_infl = (inflationRate || 0) / 100;

    const projectedSavings = futureValue(
      currentSavings || 0,
      annualContribution || 0,
      r_pre,
      yearsToRetirement
    );

    const inflationAdjustedIncome =
      (desiredAnnualIncome || 0) *
      (yearsToRetirement > 0 ? pow(1 + g_infl, yearsToRetirement) : 1);

    const requiredSavings = pvGrowingAnnuity(
      inflationAdjustedIncome,
      r_post,
      g_infl,
      retirementDuration
    );

    const shortfallSurplus = projectedSavings - requiredSavings;

    let additionalContributionNeeded = 0;
    let lumpSumNeededNow = 0;

    if (yearsToRetirement === 0) {
      lumpSumNeededNow = Math.max(0, requiredSavings - currentSavings);
    } else {
      let af;
      if (Math.abs(r_pre) < 1e-12) {
        af = yearsToRetirement;
      } else {
        const growth = pow(1 + r_pre, yearsToRetirement);
        af = (growth - 1) / r_pre;
      }

      const fvCurrent = futureValue(
        currentSavings || 0,
        annualContribution || 0,
        r_pre,
        yearsToRetirement
      );
      const neededExtra = Math.max(0, requiredSavings - fvCurrent);
      additionalContributionNeeded = af > 0 ? neededExtra / af : 0;
    }

    let postBelowInfl = "";
    if (r_post < g_infl && retirementDuration > 0 && desiredAnnualIncome > 0) {
      postBelowInfl =
        "Post-retirement return is below inflation. Keeping purchasing power may be impossible without reducing withdrawals.";
    }

    const chartData = [];
    for (let y = 0; y <= yearsToRetirement; y++) {
      const age = currentAge + y;
      const val = futureValue(currentSavings || 0, annualContribution || 0, r_pre, y);
      chartData.push({ age, savings: Math.round(val) });
    }

const inputs_json = {
  currentAge,
  retirementAge,
  currentSavings,
  annualContribution,
  preRetirementReturn,
  postRetirementReturn,
  inflationRate,
  desiredAnnualIncome,
  retirementDuration,
  year: Number(year),
  calculatorName,
};

const results_json = {
  yearsToRetirement,
  projectedSavings: Math.round(projectedSavings),
  requiredSavings: Math.round(requiredSavings),
  shortfallSurplus: Math.round(shortfallSurplus),
  additionalContributionNeeded: Math.round(additionalContributionNeeded),
  lumpSumNeededNow: Math.round(lumpSumNeededNow),
  inflationAdjustedIncome: Math.round(inflationAdjustedIncome),
  chartData,
  notes: { invalidAges, postBelowInfl },
};


    // ✅ notes for email/pdf (matches screenshot block)
    const pdf_notes = {
      disclaimer:RETIREMENT_DISCLAIMER_TEXT,
      section_title: ASSUMPTIONS_NOTES.title,
      calculation_assumptions: {
        title: ASSUMPTIONS_NOTES.leftTitle,
        bullets: ASSUMPTIONS_NOTES.assumptions,
      },
      important_disclaimers: {
        title: ASSUMPTIONS_NOTES.rightTitle,
        bullets: ASSUMPTIONS_NOTES.disclaimers,
      },
      footer_disclaimer: yearCfg.disclaimer,
    };

    return { inputs_json, results_json, pdf_notes };
  };

  const scrollToResults = () => {
    const el = resultsRef.current;
    if (el && typeof el.scrollIntoView === "function") {
      el.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  };

  const openGateFlow = () => {
    if (unlocked) {
      setShowConfirmModal(true);
      return;
    }
    setShowLeadModal(true);
  };

  const handleLeadSuccess = ({ email }) => {
    writeUnlock({ email });
    setUnlocked(true);
    setStoredEmail(email || "");
    setShowLeadModal(false);
    setShowSuccessModal(true);
  };

  const handleSuccessViewResults = () => {
    setShowSuccessModal(false);
    scrollToResults();
  };

  // ✅ UPDATED: SEND REQUEST EXACTLY LIKE FEDERAL (axios -> PROXY_URL)
  // ✅ also sends pdf_notes now
  const sendResultsToEmail = async (emailToSend) => {
    const payload = latestPayloadRef.current;
    if (!payload?.inputs_json || !payload?.results_json) return null;

    setSendingResults(true);
    try {
      const resp = await axios.post(
        PROXY_URL,
        {
          path: "/public/send-results",
          method: "POST",
          raw: true,
          qa_mode: qa,
          data: {
            action: "send_results_email",
            email: emailToSend,
            calculator_name: calculatorName,
            calculator_year: String(year),
            page_url: pageUrl,
            timestamp_utc: new Date().toISOString(),
            inputs_json: payload.inputs_json,
            results_json: payload.results_json,
            pdf_notes: payload.pdf_notes, // ✅ NEW
            schema_version: 1,
          },
        },
        {
          headers: { "Content-Type": "application/json" },
          timeout: 30000,
        }
      );

      if (resp?.data?.success === false) {
        throw new Error(resp?.data?.message || "Failed to send results email");
      }

      setShowConfirmModal(false);
      setShowUpdateEmailModal(false);
      scrollToResults();

      return resp.data;
    } catch (err) {
      console.error("sendResultsToEmail error:", err);
      return null;
    } finally {
      setSendingResults(false);
    }
  };

  const handleConfirmSend = async () => {
    if (!storedEmail) {
      setShowConfirmModal(false);
      setShowUpdateEmailModal(true);
      return;
    }
    await sendResultsToEmail(storedEmail);
  };

  const handleChangeEmail = () => {
    setShowConfirmModal(false);
    setShowUpdateEmailModal(true);
  };

  const handleUpdateEmailAndSend = async (newEmail) => {
    writeUnlock({ email: newEmail });
    setStoredEmail(newEmail);
    await sendResultsToEmail(newEmail);
  };

  const handleViewResultsClick = () => {
    const payload = computePayload();

    latestPayloadRef.current = payload;
    setResults(payload.results_json);
    setHasCalculated(true);

    openGateFlow();
    setTimeout(scrollToResults, 50);
  };

  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <div className="bg-white p-3 border rounded-lg shadow-lg">
          <p className="font-medium">Age: {label}</p>
          <p className="text-purple-600">Savings: {formatCurrency(payload[0].value)}</p>
        </div>
      );
    }
    return null;
  };

  const runCalcIfPossible = () => {
    const payload = computePayload();
    latestPayloadRef.current = payload;
    setResults(payload.results_json);
    setHasCalculated(true);
  };

  return (
    <>
      <div className="min-vh-100 bg-light">
        <div className="container py-5">
          <div className="row g-4">
            {/* LEFT: Inputs */}
            <div className="col-md-6 d-flex flex-column gap-4">
              <div className="card">
                <div className="card-header bg-success text-white d-flex align-items-center gap-2">
                  <User className="h-5 w-5" />
                  <h5 className="mb-0">Retirement Planning Details {year ? `(${year})` : ""}</h5>
                </div>

                <div className="card-body">
                  <div className="row g-3">
                    {[
                      { id: "currentAge", label: "Current Age" },
                      { id: "retirementAge", label: "Retirement Age" },
                      { id: "currentSavings", label: "Current Savings" },
                      { id: "annualContribution", label: "Annual Contribution" },
                      { id: "preRetirementReturn", label: "Pre-Retirement Return (%)" },
                      { id: "postRetirementReturn", label: "Post-Retirement Return (%)" },
                      { id: "inflationRate", label: "Inflation Rate (%)" },
                      { id: "desiredAnnualIncome", label: "Desired Annual Income" },
                      { id: "retirementDuration", label: "Retirement Duration (Years)" },
                    ].map(({ id, label }) => (
                      <div className="col-md-6" key={id}>
                        <div className="mb-2">
                          <label htmlFor={id} className="form-label fw-semibold">
                            {label}
                          </label>
                          <input
                            type="number"
                            className="form-control"
                            id={id}
                            name={id}
                            value={formData[id] ?? ""}
                            onChange={(e) => updateFormData(id, e.target.value)}
                            step="any"
                          />
                        </div>
                      </div>
                    ))}
                  </div>

                  <button
                    className="btn w-100 mt-3"
                    onClick={handleViewResultsClick}
                    style={{
                      background: "#0f8a4c",
                      borderColor: "#0f8a4c",
                      color: "white",
                      fontWeight: 700,
                    }}
                  >
                    View Results
                  </button>

                  {hasCalculated && results?.notes?.invalidAges && (
                    <div className="alert alert-danger mt-2 py-2">{results.notes.invalidAges}</div>
                  )}
                </div>
              </div>
            </div>

            {/* RIGHT: Results (✅ ALWAYS VISIBLE, blurred/locked by default) */}
            <div className="col-md-6 d-flex flex-column gap-4" ref={resultsRef}>
              <div
                className="unk-results-wrap rounded-4"
                style={{
                  background: !unlocked
                    ? "linear-gradient(180deg, rgba(255,255,255,0.92), rgba(255,255,255,0.92))"
                    : "transparent",
                }}
              >
                {!unlocked && <div className="unk-bg-blur" />}

                <div style={{ position: "relative", zIndex: 1, width: "100%" }}>
                  <div className={!unlocked ? "unk-results-blur" : ""}>
                    {hasCalculated && results ? (
                      <>
                        {/* Chart */}
                        <div className="card mb-4">
                          <div className="card-header bg-info text-white d-flex align-items-center gap-2">
                            <TrendingUp className="h-5 w-5" />
                            <h5 className="mb-0">Savings Projection</h5>
                          </div>
                          <div className="card-body">
                            <div style={{ height: "300px" }}>
                              <ResponsiveContainer width="100%" height="100%">
                                <AreaChart data={results.chartData}>
                                  <defs>
                                    <linearGradient id="colorSavings" x1="0" y1="0" x2="0" y2="1">
                                      <stop offset="5%" stopColor="#6366f1" stopOpacity={0.8} />
                                      <stop offset="95%" stopColor="#8b5cf6" stopOpacity={0.1} />
                                    </linearGradient>
                                  </defs>
                                  <CartesianGrid strokeDasharray="3 3" />
                                  <XAxis dataKey="age" />
                                  <YAxis tickFormatter={(v) => `$${(v / 1000).toFixed(0)}k`} />
                                  <Tooltip content={<CustomTooltip />} />
                                  <Area
                                    type="monotone"
                                    dataKey="savings"
                                    stroke="#6366f1"
                                    fill="url(#colorSavings)"
                                    strokeWidth={3}
                                  />
                                </AreaChart>
                              </ResponsiveContainer>
                            </div>
                          </div>
                        </div>

                        {/* Projection */}
                        <div className="card">
                          <div className="card-header bg-light d-flex align-items-center gap-2">
                            <Target className="h-5 w-5" />
                            <h5 className="mb-0">Retirement Projection</h5>
                          </div>

                          <div className="card-body">
                            <div className="row g-3 mb-3">
                              <div className="col-md-6">
                                <div className="rounded bg-primary bg-opacity-10 p-3 text-center">
                                  <div className="text-primary small fw-semibold" style={{ fontSize: "13px" }}>
                                    Projected Savings at Retirement
                                  </div>
                                  <div className="fw-bold text-primary" style={{ fontSize: "17px" }}>
                                    {formatCurrency(results.projectedSavings)}
                                  </div>
                                </div>
                              </div>

                              <div className="col-md-6">
                                <div className="rounded bg-secondary bg-opacity-10 p-3 text-center">
                                  <div className="small fw-semibold" style={{ fontSize: "13px" }}>
                                    Required Savings for Goal
                                  </div>
                                  <div className="fw-bold" style={{ fontSize: "17px" }}>
                                    {formatCurrency(results.requiredSavings)}
                                  </div>
                                </div>
                              </div>
                            </div>

                            <div
                              className={`border-start border-4 p-3 mb-3 rounded ${
                                results.shortfallSurplus < 0
                                  ? "border-danger bg-light text-danger"
                                  : "border-success bg-light text-success"
                              }`}
                            >
                              <p className="fw-semibold d-flex align-items-center gap-2 mb-0">
                                {results.shortfallSurplus < 0 ? (
                                  <>
                                    <AlertTriangle className="text-danger" />
                                    Attention needed: You have a projected shortfall of{" "}
                                    {formatCurrency(Math.abs(results.shortfallSurplus))}.
                                  </>
                                ) : (
                                  <>
                                    <CheckCircle className="text-success" />
                                    Great news! You're projected to have a surplus of{" "}
                                    {formatCurrency(results.shortfallSurplus)} at retirement.
                                  </>
                                )}
                              </p>
                            </div>

                            {results.yearsToRetirement === 0 ? (
                              <ResultRow
                                label="Immediate Lump Sum Needed"
                                value={formatCurrency(results.lumpSumNeededNow)}
                              />
                            ) : (
                              results.shortfallSurplus < 0 && (
                                <ResultRow
                                  label="Additional Annual Contribution Needed"
                                  value={formatCurrency(results.additionalContributionNeeded)}
                                />
                              )
                            )}

                            <ResultRow
                              label="Inflation-adjusted income needed"
                              value={`${formatCurrency(results.inflationAdjustedIncome)} per year`}
                            />
                            <ResultRow label="Years to retirement" value={results.yearsToRetirement} />

                            {results?.notes?.postBelowInfl && (
                              <div className="alert alert-warning mt-3">{results.notes.postBelowInfl}</div>
                            )}
                          </div>
                        </div>
                      </>
                    ) : (
                      <div className="text-muted">
                        Fill in your details, then click <strong>View Results</strong> to generate your projection.
                      </div>
                    )}
                  </div>

                  {!unlocked && (
                    <div
                      className="unk-results-overlay"
                      onClick={() => {
                        if (!hasCalculated) runCalcIfPossible();
                        openGateFlow();
                      }}
                      role="button"
                      tabIndex={0}
                      onKeyDown={(e) => {
                        if (e.key === "Enter" || e.key === " ") {
                          if (!hasCalculated) runCalcIfPossible();
                          openGateFlow();
                        }
                      }}
                      aria-label="Results locked. Open unlock modal."
                    >
                      <div className="unk-overlay-card">
                        <div className="unk-overlay-title">Results Locked</div>
                        <div className="unk-overlay-body">
                          Enter your name and email to view your results instantly.
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* ✅ UI Notes block (screenshot) */}
          <div className="bg-white rounded p-4 border mt-4">
            <h5 className="fw-semibold mb-4 d-flex align-items-center gap-2">
              {ASSUMPTIONS_NOTES.title}
            </h5>

            <div className="row">
              <div className="col-md-6 mb-3">
                <h6 className="fw-semibold">{ASSUMPTIONS_NOTES.leftTitle}:</h6>
                <ul className="text-muted small ps-3 mb-0">
                  {ASSUMPTIONS_NOTES.assumptions.map((t, i) => (
                    <li key={i}>{t}</li>
                  ))}
                </ul>
              </div>

              <div className="col-md-6 mb-3">
                <h6 className="fw-semibold">{ASSUMPTIONS_NOTES.rightTitle}:</h6>
                <ul className="text-muted small ps-3 mb-0">
                  {ASSUMPTIONS_NOTES.disclaimers.map((t, i) => (
                    <li key={i}>{t}</li>
                  ))}
                </ul>
              </div>
            </div>
          </div>

          {/* ✅ footer disclaimer from YEAR_CONFIG */}
          <div className="text-center text-muted small mt-4 w-50 mx-auto">
            <p>{yearCfg.disclaimer}</p>
          </div>
        </div>
      </div>

      {/* ===== Modals ===== */}
      <LeadGateModal
        open={showLeadModal}
        onClose={() => setShowLeadModal(false)}
        onSuccess={(payload) => handleLeadSuccess(payload)}
        calculatorName={calculatorName}
        calculatorYear={String(year)}
        pageUrl={pageUrl}
        endpoint={endpoint}
        resultsJson={latestPayloadRef.current?.results_json || results}
        inputsJson={
          latestPayloadRef.current?.inputs_json || {
            ...stableFormData,
            year: Number(year),
            calculatorName,
          }
        }
        pdf_notes={latestPayloadRef.current?.pdf_notes}
      />

      <SuccessModal
        open={showSuccessModal}
        onClose={() => setShowSuccessModal(false)}
        onViewResults={handleSuccessViewResults}
      />

      <ConfirmSendResultsModal
        open={showConfirmModal}
        email={storedEmail}
        onClose={() => setShowConfirmModal(false)}
        onViewResults={() => {
          setShowConfirmModal(false);
          scrollToResults();
        }}
        onSend={handleConfirmSend}
        onChangeEmail={handleChangeEmail}
        sending={sendingResults}
      />

      <UpdateEmailModal
        open={showUpdateEmailModal}
        initialEmail={storedEmail}
        onClose={() => setShowUpdateEmailModal(false)}
        onSend={handleUpdateEmailAndSend}
        sending={sendingResults}
        storedEmail={storedEmail}
        endpoint={PROXY_URL}
        resultsJson={latestPayloadRef.current?.results_json || results}
        inputsJson={latestPayloadRef.current?.inputs_json}
        calculatorName={calculatorName}
        calculatorYear={String(year)}
        pageUrl={pageUrl}
        pdf_notes={latestPayloadRef.current?.pdf_notes}
      />
    </>
  );
}

function ResultRow({ label, value }) {
  return (
    <div className="mt-3">
      <p className="text-sm text-gray-500 mb-1">{label}</p>
      <p className="text-lg fw-bold mb-0">{value}</p>
    </div>
  );
}

export default RetirementSavingsTaxCalculator;
